import Link from 'next/link';
import React, { type ForwardedRef } from 'react';

import LoadingIndicator from '@/components/loading-indicator';

import { StyledCompareButton, StyledPrimaryButton, StyledSecondaryButton, StyledTertiaryButton } from './styles';
import { type UrlObject } from 'url';

type LinkWrappingProps = {
  asPath?: UrlObject | string | null;
  href?: string;
  query?: UrlObject['query'];
  target?: React.HTMLAttributeAnchorTarget;
  children?: JSX.Element | React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  buttonAsDiv?: boolean;
  className?: string;
  startIcon?: JSX.Element | React.ReactNode;
  endIcon?: JSX.Element | React.ReactNode;
  onClick?: React.MouseEventHandler;
  fullWidth?: boolean;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  disableRipple?: boolean;
  count?: number;
};

type ForwardedButtonProps = {
  onClick?: React.MouseEventHandler<HTMLButtonElement> | React.MouseEventHandler<HTMLAnchorElement>;
  href?: string;
};

type WrappableButton =
  | typeof StyledPrimaryButton
  | typeof StyledSecondaryButton
  | typeof StyledTertiaryButton
  | typeof StyledCompareButton;

const handleLinkWrapping = (WrappableButtonComponent: WrappableButton, props: LinkWrappingProps) => {
  /*
    Using asPath instead of as prop here to avoid clashing with styled-components's
    reserved "as" prop: https://styled-components.com/docs/api#as-polymorphic-prop
  */
  const { asPath, href, query, target, children, disabled, isLoading, buttonAsDiv, ...rest } = props;

  const isInternalLink = href && href.startsWith('/');

  const LinkButton = React.forwardRef(
    ({ onClick, href }: ForwardedButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
      return (
        <WrappableButtonComponent
          href={href}
          ref={ref}
          disabled={disabled}
          onClick={onClick as React.MouseEventHandler<HTMLButtonElement> | undefined}
          {...rest}
        >
          {isLoading ? <LoadingIndicator /> : children}
        </WrappableButtonComponent>
      );
    }
  );

  LinkButton.displayName = 'LinkButton';

  if (href && !disabled) {
    if (isInternalLink) {
      return (
        <Link
          legacyBehavior
          passHref
          href={query ? { pathname: href, query } : href}
          as={asPath ?? undefined}
          {...rest}
        >
          <LinkButton />
        </Link>
      );
    }

    return (
      <WrappableButtonComponent
        component="a"
        href={href}
        target={target || '_blank'}
        rel={!target ? 'noopener noreferrer' : undefined}
        {...rest}
      >
        {isLoading ? <LoadingIndicator /> : children}
      </WrappableButtonComponent>
    );
  }

  const component = buttonAsDiv ? 'div' : undefined;

  return (
    <WrappableButtonComponent disabled={disabled} component={component} {...rest}>
      {isLoading ? <LoadingIndicator /> : children}
    </WrappableButtonComponent>
  );
};

const PrimaryButton = (props: LinkWrappingProps) => handleLinkWrapping(StyledPrimaryButton, props);
const SecondaryButton = (props: LinkWrappingProps) => handleLinkWrapping(StyledSecondaryButton, props);
const TertiaryButton = (props: LinkWrappingProps) => handleLinkWrapping(StyledTertiaryButton, props);
const CompareButton = (props: LinkWrappingProps) => handleLinkWrapping(StyledCompareButton, props);

export { PrimaryButton, SecondaryButton, TertiaryButton, CompareButton };
